// Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'

import {
  KeyValueTable,
  Spinner
} from '../'

const QUERY_CREDITAPPLICATION_CALCULATIONS = gql`
  query($id: ID!) {
    creditapplication(id: $id) {
      id
      incomeMonthlyBasic
      expensesMonthlyNcrMinimum
      expensesMonthlyClientTotal
      expensesMonthlyApplied
      deductionsCycleAverage
      deductionsMonthlyAverage
      debtInstalmentMonthlyNonPicsa
      debtInstalmentMonthlyNotOnPayslip
      repaymentMaxExpenseBased
      repaymentMaxPercentageBased
      repaymentMaxApplied
      interestRateMonthly
      monthlyServiceFee
      creditMaxTerm
      borrowableMax
      borrowableDisposableMax
      borrowableRiskReduced
      isFirstLoan
      productMaxTerm
      productRepaymentAvailablePercentage
      productRepaymentAvailableAmount
      productBorrowableTheoretical
      productBorrowableActual
      securityDesired
      securityAvailable
      securityShortfall
      security1pc
      security1pcRoundedUp
      securityLtContributionMinimum
      securityLtContributionActual
      picsaScore
    }
  }
`


export class CalculationVariables extends React.Component {
  static propTypes = {
    creditApplicationId: PropTypes.string.isRequired,
    calculationsLocked: PropTypes.bool,
    calculationsSnapshot: PropTypes.string
  }

  render() {
    if (this.props.calculationsLocked) {
      const { incomeMonthlyBasic, expensesMonthlyNcrMinimum, expensesMonthlyClientTotal,
        expensesMonthlyApplied, deductionsCycleAverage, deductionsMonthlyAverage, debtInstalmentMonthlyNonPicsa,
        debtInstalmentMonthlyNotOnPayslip, repaymentMaxExpenseBased, repaymentMaxPercentageBased,
        repaymentMaxApplied, interestRateMonthly, monthlyServiceFee, creditMaxTerm, borrowableMax,
        borrowableDisposableMax, borrowableRiskReduced, isFirstLoan, productMaxTerm, productRepaymentAvailablePercentage,
        productRepaymentAvailableAmount, productBorrowableTheoretical, productBorrowableActual,
        securityDesired, securityAvailable, securityShortfall, security1pc, security1pcRoundedUp,
        securityLtContributionMinimum, securityLtContributionActual, picsaScore } = this.props.calculationsSnapshot

      const variables = {
        'Basic monthly income': incomeMonthlyBasic,
        'Monthly expenses (NCR min)': expensesMonthlyNcrMinimum,
        'Monthly total expenses': expensesMonthlyClientTotal,
        'Monthly expense (applied)': expensesMonthlyApplied,
        'Deduction cycle avg': deductionsCycleAverage,
        'Deduction monthly avg': deductionsMonthlyAverage,
        'Monthly debt instalment (non-Picsa)': debtInstalmentMonthlyNonPicsa,
        'Monthly debt instalment (not on payslip)': debtInstalmentMonthlyNotOnPayslip,
        'Maximum repayment (expense based)': repaymentMaxExpenseBased,
        'Max repayment (percentage based)': repaymentMaxPercentageBased,
        'Max repayment (applied)': repaymentMaxApplied,
        'Monthly interest rate': interestRateMonthly,
        'Monthly service fee': monthlyServiceFee,
        'Max term (credit)': creditMaxTerm,
        'Borrowable (Picsa score)': picsaScore,
        'Borrowable (disposable)': borrowableDisposableMax,
        'Borrowable (risk reduced)': borrowableRiskReduced,
        'Borrowable (max)': borrowableMax,
        'First loan?': isFirstLoan ? 'Yes' : 'No',
        'Max term (product)': productMaxTerm,
        'Product repayment available (percentage)': productRepaymentAvailablePercentage,
        'Product repayment available (amount)': productRepaymentAvailableAmount,
        'Product borrowable (theoretical)': productBorrowableTheoretical,
        'Product borrowable (actual)': productBorrowableActual,
        'Security desired': securityDesired,
        'Security available': securityAvailable,
        'Security shortfall': securityShortfall,
        'Security 1%': security1pc,
        'Security 1% (rounded up)': security1pcRoundedUp,
        'Security - LT contribution min': securityLtContributionMinimum,
        'Security - LT contribution actual': securityLtContributionActual
      }

      return (
        <KeyValueTable
          heading={null}
          data={variables}
          keyColumnSize="2-of-3"
          className='slds-m-bottom_medium'
          tableClassName='slds-no-row-hover table-cells-align_right'
        />
      )
    }
    else {
      return (
        <Query
          query={QUERY_CREDITAPPLICATION_CALCULATIONS}
          variables={{ id: this.props.creditApplicationId }}
          fetchPolicy='network-only'>

          {({ loading, error, data }) => {
            if (loading || error) {
              return (
                <div style={{ marginTop: '30vh'}}>
                  <Spinner size='large' type='inverse' className='slds-is-relative'/>
                </div>
              )
            }

            const { incomeMonthlyBasic, expensesMonthlyNcrMinimum, expensesMonthlyClientTotal,
              expensesMonthlyApplied, deductionsCycleAverage, deductionsMonthlyAverage, debtInstalmentMonthlyNonPicsa,
              debtInstalmentMonthlyNotOnPayslip, repaymentMaxExpenseBased, repaymentMaxPercentageBased,
              repaymentMaxApplied, interestRateMonthly, monthlyServiceFee, creditMaxTerm, borrowableMax,
              borrowableDisposableMax, borrowableRiskReduced, isFirstLoan, productMaxTerm, productRepaymentAvailablePercentage,
              productRepaymentAvailableAmount, productBorrowableTheoretical, productBorrowableActual,
              securityDesired, securityAvailable, securityShortfall, security1pc, security1pcRoundedUp,
              securityLtContributionMinimum, securityLtContributionActual, picsaScore } = data.creditapplication

            const variables = {
              'Basic monthly income': incomeMonthlyBasic,
              'Monthly expenses (NCR min)': expensesMonthlyNcrMinimum,
              'Monthly total expenses': expensesMonthlyClientTotal,
              'Monthly expense (applied)': expensesMonthlyApplied,
              'Deduction cycle avg': deductionsCycleAverage,
              'Deduction monthly avg': deductionsMonthlyAverage,
              'Monthly debt instalment (non-Picsa)': debtInstalmentMonthlyNonPicsa,
              'Monthly debt instalment (not on payslip)': debtInstalmentMonthlyNotOnPayslip,
              'Maximum repayment (expense based)': repaymentMaxExpenseBased,
              'Max repayment (percentage based)': repaymentMaxPercentageBased,
              'Max repayment (applied)': repaymentMaxApplied,
              'Monthly interest rate': interestRateMonthly,
              'Monthly service fee': monthlyServiceFee,
              'Max term (credit)': creditMaxTerm,
              'Borrowable (Picsa score)': picsaScore,
              'Borrowable (disposable)': borrowableDisposableMax,
              'Borrowable (risk reduced)': borrowableRiskReduced,
              'Borrowable (max)': borrowableMax,
              'First loan?': isFirstLoan ? 'Yes' : 'No',
              'Max term (product)': productMaxTerm,
              'Product repayment available (percentage)': productRepaymentAvailablePercentage,
              'Product repayment available (amount)': productRepaymentAvailableAmount,
              'Product borrowable (theoretical)': productBorrowableTheoretical,
              'Product borrowable (actual)': productBorrowableActual,
              'Security desired': securityDesired,
              'Security available': securityAvailable,
              'Security shortfall': securityShortfall,
              'Security 1%': security1pc,
              'Security 1% (rounded up)': security1pcRoundedUp,
              'Security - LT contribution min': securityLtContributionMinimum,
              'Security - LT contribution actual': securityLtContributionActual
            }

            return (
              <KeyValueTable
                heading={null}
                data={variables}
                keyColumnSize="2-of-3"
                className='slds-m-bottom_medium'
                tableClassName='slds-no-row-hover table-cells-align_right'
              />
            )
          }}
        </Query>
      )
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/creditapplication-modal/CalculationVariables.js